import { Spinner, useToast } from '@chakra-ui/react'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useMemo, useState } from 'react'
import DefaultModal from '../../../../../../components/DefaultModal'
import SumsubTransactionMonitoringStatusBadge from '../../../../../../components/SumsubTransactionMonitoringStatusBadge'
import Toast from '../../../../../../components/Toast'
import TransferStatusBadge from '../../../../../../components/TransferStatusBadge'
import { INVESTMENT_PACKAGE_LABELS } from '../../../../../../constants/investmentPackages'
import { useUpdateFundDepositMutation } from '../../../../../../redux/services/coreApi'
import type {
  AdminFundDepositDto,
  TransferStatus,
} from '../../../../../../types/coreApi-types'
import { formatNumber, formatUtcDate } from '../../../../../../utils/formatters'
import StatusBox from './StatusBox'

// Section header with colored bullet (matches TransactionSummaryModal / swap modal)
const SectionHeader: React.FC<{ title: string; color: string }> = ({
  title,
  color,
}) => (
  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
    <div className={`w-2 h-2 ${color} rounded-full`}></div>
    {title}
  </h3>
)

// Info row for label-value pairs (matches TransactionSummaryModal)
const InfoRow: React.FC<{
  label: string
  value: string | React.ReactNode
  isLast?: boolean
  warning?: boolean
}> = ({ label, value, isLast = false, warning = false }) => (
  <div
    className={`flex justify-between items-center py-2 ${!isLast ? 'border-b border-gray-200 dark:border-gray-600' : ''}`}
  >
    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
      {label}
    </span>
    <span
      className={`text-sm font-mono ${
        warning
          ? 'text-amber-600 dark:text-amber-400 font-semibold'
          : 'text-gray-900 dark:text-gray-100'
      }`}
    >
      {value}
    </span>
  </div>
)

interface Props {
  isOpen: boolean
  onClose: () => void
  activeDeposit: AdminFundDepositDto | null
}

const FundDepositSummaryModal: React.FC<Props> = ({
  isOpen,
  onClose,
  activeDeposit,
}) => {
  const toast = useToast()
  const [updateFundDeposit, { isLoading }] = useUpdateFundDepositMutation()
  const [nextStatus, setNextStatus] = useState<TransferStatus | null>(null)

  const deposit = activeDeposit

  const availableStatuses = useMemo<TransferStatus[]>(
    () => [
      'UnderReview',
      'Pending',
      'Processing',
      'Completed',
      'OnHold',
      'Cancelled',
    ],
    []
  )

  if (!deposit) return null

  const toBank = deposit.toBankAccount?.bankAccountDetails

  return (
    <>
      <DefaultModal
        isLoading={false}
        isOpen={isOpen}
        onClose={onClose}
        title="Transaction Summary"
        maxW="1000px"
      >
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left column */}
            <div className="space-y-6">
              <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4">
                <SectionHeader
                  title="Transaction Information"
                  color="bg-blue-500"
                />
                <div className="space-y-0">
                  <InfoRow
                    label="Date"
                    value={formatUtcDate(deposit.createdAt)}
                  />
                  <InfoRow
                    label="Unique ID"
                    value={deposit.bankDepositUniqueId}
                    isLast
                  />
                </div>
              </div>

              <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4">
                <SectionHeader title="Deposit details" color="bg-green-500" />
                <div className="space-y-0">
                  <InfoRow
                    label="Investor"
                    value={deposit.user.name || deposit.user.email}
                  />
                  <InfoRow
                    label="Package"
                    value={
                      deposit.investmentPackage
                        ? INVESTMENT_PACKAGE_LABELS[deposit.investmentPackage]
                        : '-'
                    }
                  />
                  <InfoRow
                    label="Amount"
                    value={formatNumber(deposit.amount, deposit.currency)}
                  />
                  <InfoRow label="Currency" value={deposit.currency.isoCode} />
                  <InfoRow
                    label="Created at"
                    value={formatUtcDate(deposit.createdAt)}
                  />
                  <InfoRow
                    label="Transferred at"
                    value={formatUtcDate(
                      deposit.transferredAt || deposit.createdAt
                    )}
                  />
                  <InfoRow
                    label="Source account"
                    value={
                      deposit.fromBankAccount?.name ||
                      deposit.fromWallet?.name ||
                      deposit.fromWallet?.address ||
                      '-'
                    }
                    isLast
                  />
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-6">
              <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4">
                <SectionHeader title="Sumsub Status" color="bg-indigo-500" />
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Transfer status
                    </span>
                    <TransferStatusBadge
                      transferStatus={deposit.transferStatus}
                    />
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Sumsub status
                    </span>
                    {deposit.sumsubTransactionMonitoringStatus ? (
                      <SumsubTransactionMonitoringStatusBadge
                        status={deposit.sumsubTransactionMonitoringStatus}
                      />
                    ) : (
                      <span className="text-sm font-mono text-gray-900 dark:text-gray-100">
                        -
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Key reference data for admin to verify transfer (no instructions) */}
              <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4">
                <SectionHeader
                  title="Transfer reference"
                  color="bg-purple-500"
                />
                <div className="space-y-0">
                  <InfoRow
                    label="Amount"
                    value={formatNumber(deposit.amount, deposit.currency)}
                  />
                  {toBank?.iban && <InfoRow label="IBAN" value={toBank.iban} />}
                  <InfoRow
                    label="Description"
                    value={deposit.bankDepositUniqueId}
                    isLast
                  />
                </div>
              </div>
            </div>
          </div>

          {deposit.sumsubOnHoldReason && (
            <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-4">
              <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                On-hold reason
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-200 mt-1">
                {deposit.sumsubOnHoldReason}
              </p>
            </div>
          )}

          <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4 mt-4">
            <SectionHeader title="Change Status" color="bg-indigo-500" />
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              {availableStatuses.map(status => (
                <StatusBox
                  key={status}
                  transferStatus={status}
                  isActive={deposit.transferStatus === status}
                  onClick={() => setNextStatus(status)}
                />
              ))}
            </div>
          </div>
        </div>
      </DefaultModal>

      <DefaultModal
        isLoading={false}
        isOpen={Boolean(nextStatus)}
        onClose={() => setNextStatus(null)}
        title="Are you sure?"
        maxW="500px"
      >
        <div className="flex flex-col items-center p-10 text-description-large">
          <p className="text-center">
            Are you sure you want to perform the following action:
          </p>

          <div className="flex justify-center items-center my-4 gap-2">
            <TransferStatusBadge transferStatus={deposit.transferStatus} />
            <FontAwesomeIcon icon={faArrowRight} className="text-black" />
            {nextStatus && <TransferStatusBadge transferStatus={nextStatus} />}
          </div>

          <p>With the following ID:</p>
          <div className="mt-3 py-1 px-2 bg-black/5 rounded">
            {deposit.bankDepositUniqueId}
          </div>

          <div className="flex gap-2 w-full mt-10">
            <button
              className="button-secondary"
              onClick={() => setNextStatus(null)}
            >
              Cancel
            </button>
            <button
              className="button"
              onClick={async () => {
                if (!nextStatus) return

                await updateFundDeposit({
                  depositId: deposit.id,
                  amount: deposit.amount,
                  transferredAt: deposit.transferredAt || deposit.createdAt,
                  transferStatus: nextStatus,
                }).unwrap()

                setNextStatus(null)
                onClose()
                toast({
                  position: 'top',
                  render: () => (
                    <Toast type="success">
                      The status was successfully changed.
                    </Toast>
                  ),
                })
              }}
            >
              {isLoading ? <Spinner size="xs" /> : 'Confirm'}
            </button>
          </div>
        </div>
      </DefaultModal>
    </>
  )
}

export default FundDepositSummaryModal
